import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';

import { OnboardingKeysCombined } from '../../constants/onboarding-keys-combined.ts';

import onboardingStateGetOnboardingStateQuery, {
	type onboardingStateGetOnboardingStateQuery$data,
	type onboardingStateGetOnboardingStateQuery$variables,
} from './__generated__/onboardingStateGetOnboardingStateQuery.graphql.ts';
import { preloadAllOnboardingState } from './preloadAllOnboardingState.ts';

export const PRELOADED_KEYS = [
	'onboarding-origin-state:attributes',
	'onboarding-state:isFoundingAdmin',
	'onboarding-state:isRedactionsTourVisible',
	'onboarding-state:isContentWrapperLabelsTourDismissed',
	'onboarding-state:isMoreActionsTourVisible',
	'onboarding-state:isMoreActionsHintVisible',
	'random:ShareAndRestrictDialog:spotlightSeen',
	'home-onboarding-takeover-v2:hasSeenHomeNudge',
	'home-onboarding-takeover-v2:isHomeNudgeScheduledAt',
	'home-onboarding-takeover-v2:takeoverSeenAt',
	'home-onboarding-takeover-v2:onboardingActionStates',
	'home-onboarding-takeover-v2:isCollapsed',
	'home-onboarding-takeover-v2:hasSeenConfetti',
	'home-onboarding-takeover-v2:hasDismissedCarousel',
	'home-onboarding-takeover-v2:hasDismissedLearningResources',
	'home-onboarding-takeover-v2:hasDismissedTakeover',
	'editor-state:editorOnboardingEligiblity',
	'editor-state:editorVisited',
	'editor-state:wasDraftBannerSeen',
	'editor-state:isDraftBannerVisible',
	'editor-state:isLandInEditorBannerVisible',
	'editor-state:hasUserPublished',
	'editor-state:successFlagCompleted',
	'editor-state:recentSpotlightOpen',
	'editor-state:recentSpotlightCompleted',
	'editor-state:editorTutorialSuccessFlagCompleted',
	'editor-state:wasEditorConversionModalSeen',
	'onboarding-intent-experiences:onboardingIntentExperienceClosed',
	'onboarding-intent-experience:isOnboardingExperienceEligibleUser',
	'onboarding-intent-experiences:onboardingIntentExperienceLandViewedAt',
	'onboarding-intent-experiences:onboardingIntentExperienceLandClosed',
	'page:pagePresenceAvatarGroupSpotlightSeen',
	'page:pageTelepointerAvatarGroupSpotlightSeen',
	'whats-new-in-confluence:isWhatsNewInConfluneceSpotlightDismissed',
	'whats-new-in-confluence:whatsNewInConfluencePulseSeenAt',
	'onboarding-insights:hasContextualFloatingButtonBeenSeen',
	'trial-components:trialGracePeriodBannerDismissed',
	'trial-components:dismissedTrialnullDayFlag',
	'onboarding-contextual-invite-nudge:contextualInviteNudgeEligibleTag',
	'onboarding-contextual-invite-nudge:wasFirstPublishConfettiSeen',
	'land-in-sample-space:hasDismissedBanner',
	'land-in-sample-space:hasSeenSampleContentFlag',
	'live-page-nudge:wasLivePageNudgeSeen',
	'nav4:isNav4GlobalSpotlightDismissed',
	'nav4:isNav4SpaceTourDismissed',
	'personalized-templates-v2:onboardingActionStates',
	'personalized-templates-v2:hasDismissedOnboardingActions',
	'personalized-templates-v2:takeoverSeenAt',
	'personalized-templates-v2:isTemplateListDismissed',
	'personalized-templates-v2:templateUsageValues',
	'templates-collection:isTaggedForTemplatesCollectionExperiment',
	'templates-collection:isTemplatesSectionDismissed',
	'onboarding-state:introWorkflow',
	'onboarding-state:legacy',
	'onboarding-state:isSampleContentV2SpotlightVisible',
	'onboarding-state:landOnPath',
	'onboarding-state:teamSpaceCreated',
	'onboarding-state:improvedGetStartedPath',
	'onboarding-state:improvedGetStartedWhiteboardPath',
	'nav4:isNav4CollapseReminderSpotlightDismissed',
	'storage-enforcement-state:flagsDismissedFreeThreshold',
	'onboarding-state:nthUserOnboardingFlagViewed',
	'onboarding-state:nthUserOnboardingCompletedTimestamp',
	'onboarding-state:nthUserOnboardingPageViewed',
	'onboarding-state:nthUserGSPagePath',
	'onboarding-state:nthUserBannerDismissed',
];

export const preloadViewPageOnboardingState = () => {
	// When the centralized onboarding state experiment is enabled, use the centralized preloader
	// to ensure all onboarding state queries hit the cache.
	if (expVal('confluence_centralized_onboarding_state', 'isEnabled', false)) {
		return preloadAllOnboardingState();
	}

	return query<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>({
		query: onboardingStateGetOnboardingStateQuery,
		variables: {
			key: fg('confluence_preload_all_onboarding_keys') ? OnboardingKeysCombined : PRELOADED_KEYS,
		},
	});
};
